import { ReactNode, useEffect, useState } from 'react';
import logger from '@logger';
import { useAppDispatch } from '@hooks/useAppDispatch';
import trackCompletedSection from '@helpers/analyticsHelpers/trackCompletedSection';
import trackPlaceOrderClick from '@helpers/analyticsHelpers/trackPlaceOrderClick';
import { ErrorDataType, PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import { isAxiosError } from 'axios';
import { StyledCheckoutSubmitButton } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton.styles';
import { createKlarnaSession, placeOrder } from '@api/interfaces/checkoutApi';
import webToAppApi from '@api/web-to-app';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import { PlaceOrderErrorResponse } from '@enums/PlaceOrderErrorResponse';
import { setCompleted, setEditing, setErrorMessage, setSelectedMode } from '@slices/checkoutSlice';
import paths from '@constants/paths';
import useCustomRouter from '@hooks/useCustomRouter';
import useUserAgent from '@hooks/useUserAgent';
import { setEmptyCartModalVisibility } from '@slices/modalSlice';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import pushGTMEvent from '@helpers/analyticsHelpers/pushGTMEvent';
import ProcessingOrderModal from '@molecules/CheckoutPaymentButton/ProcessingOrderModal/ProcessingOrderModal';

interface Props {
  disabled?: boolean;
  children: ReactNode;
  possiblyWrongStoreConfirmed: PossiblyWrongStoreConfirmedState;
  setError: (error: ErrorDataType) => void;
}

const KlarnaButton = ({ disabled = false, possiblyWrongStoreConfirmed, children, setError }: Props) => {
  const [authorizeToken, setAuthorizeToken] = useState<string>('');
  const [klarnaDisabled, setKlarnaDisabled] = useState<boolean>(disabled);
  const [klarnaLoading, setKlarnaLoading] = useState<boolean>(false);
  const [showProcessingOrderModal, setShowProcessingOrderModal] = useState<boolean>(false);

  const { cart } = useCart();
  const { store } = useStore();

  const checkCartConflict = useCheckCartConflict({ checkStock: true });
  const dispatch = useAppDispatch();
  const router = useCustomRouter();

  const { isReactNative } = useUserAgent();

  const authorizeKlarna = async () => {
    setKlarnaLoading(true);
    try {
      await checkCartConflict({ storeId: store?.storeId });

      const { data } = await createKlarnaSession();
      window?.Klarna?.Payments.init({ client_token: data.client_token });

      window?.Klarna?.Payments.authorize({}, (res: any) => {
        setKlarnaLoading(false);

        if (res.show_form && res.approved) {
          setAuthorizeToken(res.authorization_token);
        } else {
          setAuthorizeToken('');
        }
      });
    } catch (e) {
      setKlarnaLoading(false);
      setAuthorizeToken('');
      if (isAxiosError(e)) {
        logger.error({ error: 'Unauthorized Klarna payment', message: e.message });
      }
    }
  };

  const submitOrder = async () => {
    setKlarnaLoading(true);
    setShowProcessingOrderModal(true);
    try {
      if (cart?.orderReference) {
        // Do conflict check only if order is open and being changed, as we then skip authorizeKlarna
        await checkCartConflict({ storeId: store?.storeId });
      }

      const response = await placeOrder({
        orderRef: cart?.orderReference,
        selectedCardId: '',
        saveCard: false,
        userClickedContinue:
          possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL ||
          possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY,
        klarnaAuthorizationToken: authorizeToken,
      });

      trackCompletedSection('payment');
      const { location } = response.headers;
      window.location.href = location;
    } catch (e: any) {
      pushGTMEvent({
        event: 'Track',
        category: 'klarna_error',
        action: `error_message_shown`,
        label: `${e.message}, ${e.request?.response}`,
        value: 0,
      });

      setKlarnaLoading(false);
      setShowProcessingOrderModal(false);
      setAuthorizeToken('');

      if (isAxiosError(e)) {
        if (e.request.response === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
          dispatch(setCompleted({ boxName: 'delivery', isCompleted: false }));
          dispatch(setEditing({ boxName: 'delivery', isEditing: true }));
          dispatch(setErrorMessage(e.request.response));
          setError({ errorCode: e.request.response });
        } else if (e.request.response === PlaceOrderErrorResponse.EMPTY_CART) {
          dispatch(setEmptyCartModalVisibility(true));
          router.push(paths.HOME);
        } else {
          setError({ errorCode: e.request.response || 'default' });
          dispatch(setSelectedMode(''));
          logger.error({ error: 'klarna error', message: e.message });
        }
      }
    }
  };

  const submitPayment = async () => {
    trackPlaceOrderClick();
    if (!cart?.orderReference) {
      await authorizeKlarna();
    } else {
      await submitOrder();
    }
  };

  useEffect(() => {
    if (authorizeToken) {
      submitOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizeToken]);

  useEffect(() => {
    setKlarnaDisabled(cart?.orderReference ? false : disabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  useEffect(() => {
    // Let the app know when the Klarna overlay is opened or closed
    if (isReactNative) {
      const containsKlarnaIframe = (nodeList: NodeList) =>
        (nodeList[0]?.firstChild as HTMLElement)?.id === 'klarna-apf-iframe';

      const observer = new MutationObserver(function (mutations) {
        mutations.forEach((mutation) => {
          if (containsKlarnaIframe(mutation.addedNodes)) {
            webToAppApi.klarnaOverlay(true);
          } else if (containsKlarnaIframe(mutation.removedNodes)) {
            webToAppApi.klarnaOverlay(false);
          }
        });
      });
      observer.observe(document.body, { childList: true });

      return () => {
        observer.disconnect();
      };
    }
  }, [isReactNative]);

  return (
    <>
      {showProcessingOrderModal && <ProcessingOrderModal />}
      <StyledCheckoutSubmitButton
        theme="primary"
        onClick={async (e) => {
          if (!(e.target as HTMLButtonElement)?.closest<HTMLFormElement>('form:invalid')) {
            await submitPayment();
          }
        }}
        type="submit"
        data-testid="klarna-checkout-button"
        disabled={klarnaDisabled}
        isSubmitting={klarnaLoading}
      >
        {children}
      </StyledCheckoutSubmitButton>
    </>
  );
};

export default KlarnaButton;
